/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Multi-context drawer session state for Command Intelligence. Tracks attached
 * context items (0..N), available pool, and mode for header / picker affordances.
 */

import { useCallback, useMemo, useState } from 'react';

import { DEFAULT_MAX_ATTACHED_ITEMS, SESSION_MODES } from './constants';

const defaultGetItemKey = item => {
  if (!item || typeof item !== 'object') {
    return '';
  }

  return item.crn ?? item.ref ?? item.orderRef ?? item.id ?? '';
};

/**
 * @param {unknown[]} items
 * @param {(item: unknown) => string} getItemKey
 * @returns {string}
 */
export const buildAvailableItemsFingerprint = (items, getItemKey = defaultGetItemKey) => {
  if (!Array.isArray(items) || items.length === 0) {
    return '';
  }

  return items
    .map(item => getItemKey(item))
    .filter(Boolean)
    .join('|');
};

const deriveMode = (items, requestedMode, getItemKey) => {
  const count = Array.isArray(items) ? items.length : 0;

  if (requestedMode === SESSION_MODES.FILTERED_LIST) {
    return SESSION_MODES.FILTERED_LIST;
  }

  if (count === 0) {
    return SESSION_MODES.BLANK;
  }

  if (count === 1) {
    return SESSION_MODES.SINGLE;
  }

  return SESSION_MODES.MULTI;
};

const dedupeAndCap = (items, getItemKey, maxAttachedItems) => {
  const seen = new Set();
  const result = [];

  for (const item of items) {
    const key = getItemKey(item);

    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(item);

    if (result.length >= maxAttachedItems) {
      break;
    }
  }

  return result;
};

const createInitialState = () => ({
  open: false,
  mode: SESSION_MODES.BLANK,
  attachedItems: [],
  availableItems: [],
  listFilters: null,
});

export const useCommandIntelligenceSession = ({
  getItemKey = defaultGetItemKey,
  maxAttachedItems = DEFAULT_MAX_ATTACHED_ITEMS,
} = {}) => {
  const [session, setSession] = useState(createInitialState);

  const openBlank = useCallback(({ listFilters, availableItems } = {}) => {
    setSession({
      open: true,
      mode: SESSION_MODES.BLANK,
      attachedItems: [],
      availableItems: Array.isArray(availableItems) ? availableItems : [],
      listFilters: listFilters ?? null,
    });
  }, []);

  const openForItem = useCallback(
    (item, { availableItems: pool } = {}) => {
      const attached = item ? dedupeAndCap([item], getItemKey, maxAttachedItems) : [];

      setSession({
        open: true,
        mode: SESSION_MODES.SINGLE,
        attachedItems: attached,
        availableItems: Array.isArray(pool) ? pool : [],
        listFilters: null,
      });
    },
    [getItemKey, maxAttachedItems]
  );

  const openForItemList = useCallback(
    ({ itemList, listFilters, availableItems: pool } = {}) => {
      const attached = Array.isArray(itemList) ? dedupeAndCap(itemList, getItemKey, maxAttachedItems) : [];
      const availablePool = Array.isArray(pool) && pool.length > 0 ? pool : attached;

      setSession({
        open: true,
        mode: SESSION_MODES.FILTERED_LIST,
        attachedItems: attached,
        availableItems: availablePool,
        listFilters: listFilters ?? null,
      });
    },
    [getItemKey, maxAttachedItems]
  );

  const updateAvailableItems = useCallback(
    availableItems => {
      if (!Array.isArray(availableItems)) {
        return;
      }

      setSession(previous => {
        if (!previous.open) {
          return previous;
        }

        const nextFp = buildAvailableItemsFingerprint(availableItems, getItemKey);
        const prevFp = buildAvailableItemsFingerprint(previous.availableItems, getItemKey);

        if (nextFp === prevFp) {
          return previous;
        }

        return { ...previous, availableItems };
      });
    },
    [getItemKey]
  );

  const hydrateItemFromPool = useCallback(
    (item, pool) => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const key = getItemKey(item);

      if (!key || !Array.isArray(pool) || pool.length === 0) {
        return item;
      }

      const match = pool.find(candidate => getItemKey(candidate) === key);

      return match ?? item;
    },
    [getItemKey]
  );

  const attachItems = useCallback(
    newItems => {
      if (!Array.isArray(newItems) || newItems.length === 0) {
        return;
      }

      setSession(previous => {
        const pool = [...previous.availableItems, ...previous.attachedItems];
        const hydrated = newItems.map(item => hydrateItemFromPool(item, pool));
        const merged = dedupeAndCap([...previous.attachedItems, ...hydrated], getItemKey, maxAttachedItems);

        return {
          ...previous,
          attachedItems: merged,
          mode: deriveMode(
            merged,
            previous.mode === SESSION_MODES.FILTERED_LIST ? SESSION_MODES.FILTERED_LIST : undefined,
            getItemKey
          ),
        };
      });
    },
    [getItemKey, maxAttachedItems, hydrateItemFromPool]
  );

  const detachItem = useCallback(
    itemKey => {
      if (!itemKey) {
        return;
      }

      setSession(previous => {
        const next = previous.attachedItems.filter(item => getItemKey(item) !== itemKey);

        return {
          ...previous,
          attachedItems: next,
          mode: deriveMode(
            next,
            previous.mode === SESSION_MODES.FILTERED_LIST ? SESSION_MODES.FILTERED_LIST : undefined,
            getItemKey
          ),
        };
      });
    },
    [getItemKey]
  );

  const clearAttached = useCallback(() => {
    setSession(previous => ({
      ...previous,
      attachedItems: [],
      mode: SESSION_MODES.BLANK,
    }));
  }, []);

  const clearContext = useCallback(() => {
    setSession(previous => ({
      ...previous,
      mode: SESSION_MODES.BLANK,
      attachedItems: [],
      availableItems: [],
      listFilters: null,
    }));
  }, []);

  const close = useCallback(() => {
    setSession(previous => ({ ...previous, open: false }));
  }, []);

  const reset = useCallback(() => {
    setSession(createInitialState());
  }, []);

  const derived = useMemo(
    () => ({
      hasContext: session.attachedItems.length > 0,
      attachedCount: session.attachedItems.length,
    }),
    [session.attachedItems]
  );

  return {
    session,
    derived,
    openBlank,
    openForItem,
    openForItemList,
    updateAvailableItems,
    attachItems,
    detachItem,
    clearAttached,
    clearContext,
    close,
    reset,
  };
};

export { defaultGetItemKey };
